/* op log 是唯一真相來源：同步、版本歷史、審計三者共用同一種結構。
   一條改動只寫一次、只加密一次、只傳一次；欄位級 diff 讓 20 個版本幾乎不佔空間。 */
import type { EntityType, FieldDelta, OpReason, SyncOp, ULID } from "./types.ts";
import { DAY_MS, VERSION_KEEP_COUNT, VERSION_KEEP_DAYS } from "./rules.ts";

export interface MakeOpArgs {
  entityType: EntityType;
  entityId: ULID;
  baseRev: number;
  changedFields: Record<string, FieldDelta>;
  reason: OpReason;
  deviceId: string;
  seq: number;
  at?: number;
  idFor: () => string;
}

export function makeOp(a: MakeOpArgs): SyncOp {
  return {
    opId: a.idFor(),
    deviceId: a.deviceId,
    seq: a.seq,
    at: a.at ?? Date.now(),
    entityType: a.entityType,
    entityId: a.entityId,
    baseRev: a.baseRev,
    changedFields: a.changedFields,
    reason: a.reason,
  };
}

/** 只比較真的有變的欄位；沒有變動就回傳空物件（呼叫端據此決定要不要寫 op）。 */
export function diffFields<T extends object>(before: T, after: Partial<T>): Record<string, FieldDelta> {
  const src = before as Record<string, unknown>;
  const out: Record<string, FieldDelta> = {};
  for (const key of Object.keys(after)) {
    const to = (after as Record<string, unknown>)[key];
    if (JSON.stringify(src[key]) !== JSON.stringify(to)) out[key] = { from: src[key], to };
  }
  return out;
}

export function collectOps(ops: SyncOp[], entityId: ULID): SyncOp[] {
  return ops.filter((o) => o.entityId === entityId).sort((a, b) => a.seq - b.seq);
}

/** 記帳欄位不參與衝突判定：每個 op 都會動到 updatedAt，
    把它算進去會讓「兩個人各改不同欄位」也被誤判成衝突。 */
const BOOKKEEPING = new Set(["updatedAt", "rev"]);

export function realFields(op: SyncOp): string[] {
  return Object.keys(op.changedFields).filter((f) => !BOOKKEEPING.has(f));
}

/** 套用一個 op。
    版本號取 max(目前, baseRev)+1 而不是 baseRev+1：
    遠端 op 的 baseRev 可能落後於本機（中間合併過），用後者會讓版本號倒退。 */
export function applyOp<T extends { rev: number }>(entity: T, op: SyncOp): T {
  const next: Record<string, unknown> = { ...(entity as unknown as Record<string, unknown>) };
  for (const [field, delta] of Object.entries(op.changedFields)) next[field] = delta.to;
  next["rev"] = Math.max(entity.rev, op.baseRev) + 1;
  return next as unknown as T;
}

/** 回到指定版本：從最新往回，把每個 op 的 from 值寫回去。 */
export function restoreTo<T extends { id: ULID; rev: number }>(
  entity: T, ops: SyncOp[], targetRev: number,
): T {
  const out: Record<string, unknown> = { ...(entity as unknown as Record<string, unknown>) };
  const hist = collectOps(ops, entity.id).sort((a, b) => b.baseRev - a.baseRev);
  for (const op of hist) {
    const rev = out["rev"] as number;
    if (rev <= targetRev) break;
    if (op.baseRev < targetRev) continue;
    for (const [field, delta] of Object.entries(op.changedFields)) out[field] = delta.from;
    out["rev"] = op.baseRev;
  }
  return out as unknown as T;
}

/** 保留策略：每條知識最近 N 版、或 M 天內全部版本（先到者為準）。 */
export function pruneOps(
  ops: SyncOp[], now: number,
  keepCount = VERSION_KEEP_COUNT, keepDays = VERSION_KEEP_DAYS,
): SyncOp[] {
  const cutoff = now - keepDays * DAY_MS;
  const byEntity = new Map<string, SyncOp[]>();
  for (const op of ops) {
    const list = byEntity.get(op.entityId) ?? [];
    list.push(op);
    byEntity.set(op.entityId, list);
  }
  const keep = new Set<string>();
  for (const list of byEntity.values()) {
    const sorted = [...list].sort((a, b) => b.at - a.at);
    sorted.slice(0, keepCount).forEach((o) => keep.add(o.opId));
    for (const o of sorted) if (o.at >= cutoff) keep.add(o.opId);
  }
  return ops.filter((o) => keep.has(o.opId));
}

/** 衝突判定：進來的 op 不是接在本機目前版本之後，且動到了本機也動過的欄位。 */
/** 哪一筆 op 勝出：先比時間，再比裝置 ID —— 兩端算出來的結果必須一致。 */
export function beats(a: SyncOp, b: SyncOp): boolean {
  if (a.at !== b.at) return a.at > b.at;
  return a.deviceId > b.deviceId;
}

/** 回傳本機與 incoming 真正衝突的那些 op（同一欄位、且在本機較新的版本之後）。 */
export function conflictingOps(incoming: SyncOp, localOps: SyncOp[]): SyncOp[] {
  const incomingFields = new Set(realFields(incoming));
  if (!incomingFields.size) return [];
  return localOps.filter((o) => o.baseRev >= incoming.baseRev && o.opId !== incoming.opId
    && realFields(o).some((f) => incomingFields.has(f)));
}

export function conflictsWith(incoming: SyncOp, localOps: SyncOp[]): boolean {
  const overlapping = localOps.filter(
    (o) => o.baseRev >= incoming.baseRev && o.opId !== incoming.opId,
  );
  if (!overlapping.length) return false;
  const incomingFields = new Set(realFields(incoming));
  if (!incomingFields.size) return false;
  return overlapping.some((o) => realFields(o).some((f) => incomingFields.has(f)));
}

export interface VersionEntry { rev: number; at: number; reason: OpReason; fields: string[] }

export function versionHistory<T extends { id: ULID; rev: number }>(
  entity: T, ops: SyncOp[],
): VersionEntry[] {
  const entries: VersionEntry[] = [{ rev: entity.rev, at: entity.rev, reason: "seed", fields: [] }];
  const list = collectOps(ops, entity.id).map((o) => ({
    rev: o.baseRev + 1, at: o.at, reason: o.reason, fields: Object.keys(o.changedFields),
  }));
  return [...entries.slice(0, 0), ...list].sort((a, b) => b.rev - a.rev);
}
